/**
 * Shared property/text formatting helpers — Feb 25, 2026 (P1).
 *
 * Every component that renders a property address, city, county, or state
 * MUST go through these helpers so the same row looks identical no matter
 * its source (manual entry vs. bulk upload) and no matter where it's
 * displayed (Orders dashboard, Order Details, Reports, etc.).
 *
 * Rules (locked in with the user):
 *   • Hyphenated names → title-case each segment   (Miami-Dade, Wilkes-Barre)
 *   • Multi-word names → title-case each word      (San Bernardino, New Castle)
 *   • State codes (2-letter)                        → ALWAYS uppercase
 *   • Directionals  N/S/E/W/NE/NW/SE/SW            → ALWAYS uppercase
 *   • Suffixes/acronyms LLC/INC/LLP/PC/USA/US/PO/  → ALWAYS uppercase
 *     IRA/HOA/COA + Roman numerals II–X
 *   • Ordinals 1ST/22ND/33RD/4TH                   → 1st/22nd/33rd/4th
 *   • Mc prefix (MCDONALD)                         → McDonald
 *   • Apostrophes (O'BRIEN, D'AMICO)               → O'Brien, D'Amico
 *   • Placeholders (TBD, NOT DEFINED, n/a, etc.)   → em-dash "—"
 */

// Tokens that must stay UPPERCASE (case-insensitive match against the token).
const ACRONYMS_UPPER = new Set([
  'LLC', 'INC', 'CORP', 'LLP', 'LP', 'PC', 'PA', 'PLC',
  'USA', 'US', 'PO', 'IRA', 'HOA', 'COA',
  // Roman numerals — common in name suffixes ("John Smith III")
  'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
]);

const DIRECTIONALS = new Set(['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW']);

const ORDINAL_RE = /^(\d+)(ST|ND|RD|TH)$/i;
// Digits + trailing letters (12B, 4A, 101AB) — unit/apartment identifiers. Checked AFTER ordinals.
const UNIT_ID_RE = /^(\d+)([A-Za-z]{1,2})$/;

// Mirror of the backend `_HARD_INVALID_PROPERTY_PLACEHOLDERS ∪ _NA_PROPERTY_PLACEHOLDERS`.
// Kept here as well so legacy MongoDB rows already storing placeholders render
// cleanly without any backfill.
const PLACEHOLDER_VALUES = new Set([
  '', 'n/a', 'na', 'tbd', 'tb',
  'not defined', 'not_defined', 'notdefined',
  'unknown', 'missing', 'pending',
  'null', 'none',
  '-', '--',
]);

export const EMPTY_PLACEHOLDER = '—';

/** True when value is present and not a known placeholder string. */
export function isMeaningfulValue(value) {
  if (value === null || value === undefined) return false;
  const cleaned = String(value).trim().toLowerCase();
  if (!cleaned) return false;
  return !PLACEHOLDER_VALUES.has(cleaned);
}

function _capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function _titleCaseToken(token) {
  if (!token) return '';
  const upper = token.toUpperCase();

  if (ACRONYMS_UPPER.has(upper)) return upper;
  if (DIRECTIONALS.has(upper)) return upper;

  // 1ST → 1st, 22ND → 22nd
  const ord = ORDINAL_RE.exec(token);
  if (ord) return `${ord[1]}${ord[2].toLowerCase()}`;

  // Unit identifiers with a trailing letter (12B, 4A) keep the letter UPPERCASE (PDF-ADDR-CASE-002)
  const unit = UNIT_ID_RE.exec(token);
  if (unit) return `${unit[1]}${unit[2].toUpperCase()}`;

  // Hyphenated → recurse on each segment (Miami-Dade, Wilkes-Barre)
  if (token.includes('-')) {
    return token.split('-').map(_titleCaseToken).join('-');
  }

  // Apostrophes (O'Brien, D'Amico) — capitalize each side
  if (token.includes("'")) {
    return token.split("'").map(_capitalize).join("'");
  }

  // Mc prefix (MCDONALD → McDonald)
  if (upper.startsWith('MC') && token.length > 2) {
    return 'Mc' + _capitalize(token.slice(2));
  }

  return _capitalize(token);
}

/**
 * Convert ALL-CAPS / mixed-case text to Title Case using the rules above.
 * Returns an empty string for missing / placeholder input — callers should
 * usually use `formatCity / formatCounty / formatStreetAddress` instead so
 * they get the em-dash fallback automatically.
 */
export function toTitleCase(input) {
  if (!isMeaningfulValue(input)) return '';
  return String(input).trim().split(/\s+/).map(_titleCaseToken).join(' ');
}

export function formatCity(value) {
  return isMeaningfulValue(value) ? toTitleCase(value) : EMPTY_PLACEHOLDER;
}

export function formatCounty(value) {
  return isMeaningfulValue(value) ? toTitleCase(value) : EMPTY_PLACEHOLDER;
}

export function formatStreetAddress(value) {
  return isMeaningfulValue(value) ? toTitleCase(value) : EMPTY_PLACEHOLDER;
}

/** State codes stay UPPERCASE per US postal convention (CA, DE, NY). */
export function formatState(value) {
  return isMeaningfulValue(value) ? String(value).trim().toUpperCase() : EMPTY_PLACEHOLDER;
}

/**
 * Compose a full single-line address: "123 Main St, Springfield, IL 62701".
 * Skips empty / placeholder parts. Returns em-dash if everything is missing.
 *
 * @param parts - { street, city, state, zip }
 */
export function formatFullAddress(parts) {
  if (!parts) return EMPTY_PLACEHOLDER;
  const street = isMeaningfulValue(parts.street) ? toTitleCase(parts.street) : null;
  const city = isMeaningfulValue(parts.city) ? toTitleCase(parts.city) : null;
  const state = isMeaningfulValue(parts.state) ? String(parts.state).trim().toUpperCase() : null;
  const zip = isMeaningfulValue(parts.zip) ? String(parts.zip).trim() : null;

  const segments = [];
  if (street) segments.push(street);
  if (city) segments.push(city);
  if (state) segments.push(zip ? `${state} ${zip}` : state);
  return segments.length > 0 ? segments.join(', ') : EMPTY_PLACEHOLDER;
}
